import { useState } from "react";
import { ChevronDown, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
} from "@/components/ui/library/dropdown-menu";
import { ChatOptions } from "./ChatOptions";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";

interface ChatHeaderProps {
  selectedLabel: string;
  selectedChildId: number | null;
  children: any[];
  onSelectChat: (childId: number | null) => void;
}

export function ChatHeader({
  selectedLabel,
  selectedChildId,
  children,
  onSelectChat,
}: ChatHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [submenuKey, setSubmenuKey] = useState(0);
  const { lead } = useLeadForm();

  // Get team members for display
  const leadUsers: { id: number; name: string }[] = (lead?.LeadUsers || []).map(
    (lu: any) => ({
      id: lu.user?.id || lu.userId,
      name: lu.user?.name || "Unknown",
    })
  );

  const handleAllChatsSelect = () => {
    onSelectChat(null);
    setIsOpen(false);
    setSubmenuKey((prev) => prev + 1);
  };

  const handleChildSelect = (childId: number) => {
    onSelectChat(childId);
    setIsOpen(false);
    setSubmenuKey((prev) => prev + 1);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSubmenuKey((prev) => prev + 1);
  };

  const memberNames = leadUsers.map((u) => u.name).slice(0, 3);
  const memberSuffix = leadUsers.length > 3 ? ` +${leadUsers.length - 3}` : "";

  return (
    <div className="bg-[var(--color-primary)] flex gap-3 h-14 items-center px-5 py-2.5 rounded-tl-[16px] rounded-tr-[16px] w-full">
      {/* Stacked user avatars instead of chat icon */}
      <div className="flex -space-x-1.5 shrink-0">
        {leadUsers.slice(0, 3).map((u, i) => (
          <div
            key={u.id}
            className="size-8 rounded-full bg-white/20 border-2 border-[var(--color-primary)] flex items-center justify-center"
            style={{ zIndex: 3 - i }}
            title={u.name}
          >
            <span className="text-[9px] font-semibold text-white">
              {u.name
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </span>
          </div>
        ))}
        {leadUsers.length === 0 && (
          <div className="size-8 rounded-full bg-white/20 flex items-center justify-center">
            <Users size={16} className="text-white" />
          </div>
        )}
      </div>

      {/* Title + channel selector */}
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <div className="flex flex-col items-start flex-1 min-w-0 cursor-pointer">
            <div className="flex gap-1.5 items-center">
              <p className="text-[14px] font-semibold text-white tracking-[-0.28px] leading-normal whitespace-nowrap">
                {selectedLabel}
              </p>
              <ChevronDown
                size={16}
                className="text-white/70 shrink-0"
                strokeWidth={2}
              />
            </div>
            <p className="text-[10px] text-white/60 truncate max-w-[250px]">
              {memberNames.length > 0
                ? `${memberNames.join(", ")}${memberSuffix} · Re: ${lead?.name || ""}`
                : `Team chat · Re: ${lead?.name || ""}`}
            </p>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          className="w-[200px] bg-white border border-[var(--color-black-1)] rounded-[16px] p-1 gap-1"
        >
          <DropdownMenuItem
            className={`h-9 pl-2.5 pr-1.5 py-2 rounded-[12px] cursor-pointer gap-1 ${
              !selectedChildId ? "bg-[#eaf5f1]" : "hover:bg-gray-50"
            }`}
            onSelect={(e) => {
              e.preventDefault();
              handleAllChatsSelect();
            }}
          >
            <p className="text-[14px] font-medium tracking-[-0.56px] leading-[1.35] flex-1">
              All Chats
            </p>
          </DropdownMenuItem>

          {children.length > 0 && (
            <DropdownMenuSub key={submenuKey}>
              <DropdownMenuSubTrigger className="h-9 pl-2.5 pr-1.5 py-2 rounded-[12px] cursor-pointer gap-1">
                <p className="text-[14px] font-normal text-[var(--color-black-8)] tracking-[-0.56px] leading-[1.35] flex-1">
                  Application Chats
                </p>
              </DropdownMenuSubTrigger>
              <ChatOptions
                children={children}
                selectedChildId={selectedChildId}
                onSelectChild={handleChildSelect}
              />
            </DropdownMenuSub>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Member count badge */}
      <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 shrink-0">
        <Users size={12} className="text-white/70" />
        <span className="text-[10px] text-white/70 font-medium">
          {leadUsers.length}
        </span>
      </div>
    </div>
  );
}
